import { PayloadAction } from '@reduxjs/toolkit';
import { DataState } from '../../types/asyncData';
import { createSliceCustom } from '../../utils/tests/createSliceCustom';
import { recentSowChangesStoreKey } from './recentSowChanges.const';
import {
  downloadRecentSowChanges,
  fetchRecentSowChanges,
} from './recentSowChanges.thunk';
import {
  RecentOperationalChangesSortPayloadType,
  RecentSOWChangesState,
  SortRecentOperationalChangesBy,
} from './recentSowChanges.types';
import {
  executeSowChangeReq,
  rejectSowChangeReq,
} from './recentSowChanges.thunk';
import {
  BusinessAction,
  ChReqStatus,
  ExecuteSowChangeReqRespDto,
  SowChangeReqFullDto,
} from '../../types/sow-change-req';
import { SortOrder, UserProfileDto } from '../../types';

// const handleDownloadCSV = (reports: any[]) => {
//   const reportData = reports.map((report) => {
//     const changedData = CHANGE_FIELDS_MAP.reduce(
//       (acc: string, { label, oldKey, newKey }) => {
//         const oldValue = report[oldKey] as string;
//         const newValue = report[newKey] as string;

//         if (oldValue !== newValue) {
//           acc =
//             acc +
//             (acc ? `, ` : `change `) +
//             `${label} from ${oldValue} to ${newValue}`;
//         }
//         return acc;
//       },
//       '',
//     );
//     const appendToSheetStatus = report['extOperationsStatus'].find(
//       (data) => data.action === ExtOperType.APPEND_TO_SHEET,
//     )?.status;

//     const appendToZohoStatus = report['extOperationsStatus'].find(
//       (data) => data.action === ExtOperType.CHANGE_PROJECT_IN_ZP,
//     )?.status;

//     return {
//       ['Date']: report['createdAt'],
//       ['Person or Entity']: report['entity'],
//       ['Account']: report['accountName'],
//       ['Project']: report['projectName'],
//       ['Changes']: changedData,
//       ['Author']: report['raisedBy'],
//       ['SOW sync status']: appendToSheetStatus
//         ? ExtOperStatusLabel[appendToSheetStatus]
//         : '-',
//       ['Zoho People sync status']: appendToZohoStatus
//         ? ExtOperStatusLabel[appendToZohoStatus]
//         : '-',
//       ['Email sent status']: appendToZohoStatus
//         ? ExtOperStatusLabel[appendToZohoStatus]
//         : '-',
//       ['Resolution']:
//         report['reqStatus'] === ChReqStatus.DONE
//           ? 'Approved'
//           : report['reqStatus'] === ChReqStatus.REJECTED
//           ? 'Declined'
//           : 'Pending',
//       ['Reviewed By']: report['approvedBy'] ?? 'Pending review',
//     };
//   });

//   const csvData = Papaparse.unparse([], {
//     quotes: true,
//     columns: [
//       'Date',
//       'Person or Entity',
//       'Account',
//       'Project',
//       'Changes',
//       'Author',
//       'SOW sync status',
//       'Zoho People sync status',
//       'Email sent status',
//       'Resolution',
//       'Reviewed By',
//     ],
//   });

//   downloadCsv(csvData, 'Recent operational changes');
// };

export const initialState: RecentSOWChangesState = {
  currentPage: 1,
  pageSize: 20,
  data: [],
  reportData: [],
  cache: {},
  totalPages: 0,
  isLoading: DataState.Idle,
  isReportLoading: DataState.Idle,

  executeSowChangeReqState: DataState.Idle,
  executeSowChangeReqError: null,

  rejectSowChangeReqState: DataState.Idle,
  rejectSowChangeReqError: null,

  sowChangeReqId: null,

  filters: {
    authorIds: [],
    projectIds: [],
    accountIds: [],
    userIds: [],
    actions: [],
    reqStatus: [],
    approvedBy: [],
    fromAndToDate: [],
    reviewerIds: [],
  },
  areFiltersApplied: false,

  sortBy: SortRecentOperationalChangesBy.Date,
  sortOrder: SortOrder.Descending,
};

export const recentSowChanges = createSliceCustom({
  name: recentSowChangesStoreKey,
  initialState,
  reducers: {
    setPagination: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    setFromCachedData: (state, { payload }: PayloadAction<string>) => {
      state.data = state.cache[payload];
    },
    addFilter: (
      state,
      {
        payload: { name, value },
      }: PayloadAction<{ name: string; value: string | string[] }>,
    ) => {
      if (state.filters[name]) {
        if (typeof value === 'string') state.filters[name].push(value);
        else state.filters[name] = value;
      }
      if (name === 'fromAndToDate') {
        state.cache = {};
        state.currentPage = 1;
      } else state.areFiltersApplied = false;
    },
    removeFilter: (
      state,
      {
        payload: { name, value },
      }: PayloadAction<{ name: string; value: string }>,
    ) => {
      const updatedFilterData = state.filters[name].filter(
        (filterValue) => filterValue !== value,
      );
      state.filters[name] = updatedFilterData;
      state.areFiltersApplied = false;
    },
    applyFilter: (state) => {
      state.cache = {};
      state.currentPage = 1;
      state.areFiltersApplied = true;
    },
    resetFilters: (state) => {
      state.filters = {
        ...initialState.filters,
        fromAndToDate: state.filters.fromAndToDate,
      };
      state.areFiltersApplied = false;
      state.cache = {};
      state.currentPage = 1;
    },
    resetCacheAndPage: (state) => {
      state.cache = {};
      state.currentPage = 1;
      state.totalPages = 0;
    },
    setSortBy: (
      state,
      { payload }: PayloadAction<RecentOperationalChangesSortPayloadType>,
    ) => {
      state.sortBy = payload.sortBy;
      state.sortOrder = payload.sortOrder ?? SortOrder.Ascending;
    },
  },
  extraReducers: (builder) => {
    // fetch recent changes
    builder
      .addCase(fetchRecentSowChanges.pending, (state) => {
        state.isLoading = DataState.Pending;
      })
      .addCase(fetchRecentSowChanges.fulfilled, (state, { payload }) => {
        state.currentPage = payload.currentPage;
        state.totalPages = payload.totalPages;
        state.cache[`${state.currentPage}${state.sortBy}${state.sortOrder}`] =
          payload.data;
        state.data = payload.data;
        state.isLoading = DataState.Fulfilled;
      })
      .addCase(fetchRecentSowChanges.rejected, (state) => {
        state.isLoading = DataState.Rejected;
      });

    builder
      .addCase(downloadRecentSowChanges.pending, (state) => {
        state.isReportLoading = DataState.Pending;
      })
      .addCase(downloadRecentSowChanges.fulfilled, (state, {}) => {
        // handleDownloadCSV(payload.data);
        state.isReportLoading = DataState.Fulfilled;
      })
      .addCase(downloadRecentSowChanges.rejected, (state) => {
        state.isReportLoading = DataState.Rejected;
      });

    builder
      .addCase(executeSowChangeReq.pending, (state, action) => {
        state.executeSowChangeReqState = DataState.Pending;
        state.executeSowChangeReqError = initialState.executeSowChangeReqError;
        state.sowChangeReqId = action.meta.arg.id;
      })
      .addCase(
        executeSowChangeReq.fulfilled,
        (state, { payload }: PayloadAction<ExecuteSowChangeReqRespDto>) => {
          state.executeSowChangeReqState = DataState.Fulfilled;
          const sowChangeReq = state.data.find(
            (data) => data.id === state.sowChangeReqId,
          );

          if (sowChangeReq) {
            sowChangeReq.reqStatus = ChReqStatus.DONE;
            if (sowChangeReq.action === BusinessAction.EDIT_RESOURCE) {
              sowChangeReq.change.sowChangeStatus =
                payload.sowAppendStatus ?? null;
              sowChangeReq.approvedOn = payload.updatedResources[0].createdAt;
            } else if (
              sowChangeReq.change.action === BusinessAction.MOVE_RESOURCE
            ) {
              sowChangeReq.change.sowChangeStatus =
                payload.sowAppendStatus ?? null;
              sowChangeReq.change.zpChangeStatus =
                payload.emailSentStatus ?? null;
              sowChangeReq.approvedOn = payload.createdResources[0].createdAt;
            }

            sowChangeReq.approvedBy = {
              fullName: payload.approvedByName,
            } as UserProfileDto;
          }
        },
      )
      .addCase(executeSowChangeReq.rejected, (state) => {
        state.executeSowChangeReqState = DataState.Rejected;
        state.executeSowChangeReqError = `Oops. Something went wrong. :(`;
        state.sowChangeReqId = null;
      });

    builder
      .addCase(rejectSowChangeReq.pending, (state, action) => {
        state.rejectSowChangeReqState = DataState.Pending;
        state.rejectSowChangeReqError = initialState.rejectSowChangeReqError;
        state.sowChangeReqId = action.meta.arg.id;
      })
      .addCase(
        rejectSowChangeReq.fulfilled,
        (state, { payload }: PayloadAction<SowChangeReqFullDto>) => {
          state.rejectSowChangeReqState = DataState.Fulfilled;
          const indexOfUpdatedOperationalChange = state.data.findIndex(
            (data) => data.id === state.sowChangeReqId,
          );
          if (indexOfUpdatedOperationalChange >= 0) {
            state.data[indexOfUpdatedOperationalChange].reqStatus =
              ChReqStatus.REJECTED;
            state.data[indexOfUpdatedOperationalChange].approvedBy =
              payload.approvedBy;
            state.data[indexOfUpdatedOperationalChange].approvedOn =
              payload.approvedOn;
            state.data[indexOfUpdatedOperationalChange].declineNotes =
              payload.declineNotes;
          } // TODO HERE?
        },
      )
      .addCase(rejectSowChangeReq.rejected, (state) => {
        state.rejectSowChangeReqState = DataState.Rejected;
        state.rejectSowChangeReqError = `Oops. Something went wrong. :(`;
      });
  },
});

export const recentSowChangesSlice = recentSowChanges.slice;
export const recentSowChangesExtraReducers = recentSowChanges.extraReducers;
export const {
  setPagination,
  setFromCachedData,
  addFilter,
  removeFilter,
  applyFilter,
  resetFilters,
  resetCacheAndPage,
  setSortBy,
} = recentSowChangesSlice.actions;
